"use client";

import { Loader2Icon, PauseIcon, PlayIcon, RotateCcwIcon } from "lucide-react";
import { cn } from "../../../lib";
import { Button } from "../../button";
import { Orb } from "../../react-bits/orb";
import type { InteractiveAudioTransportState } from "./use-interactive-audio-transport";

const ORB_HOST_CLASSNAME =
	"relative isolate flex size-[calc(1.75rem*1.625-5px)] shrink-0 items-center justify-center overflow-visible";

const ORB_BACKDROP_CLASSNAME =
	"pointer-events-none absolute inset-0 z-0 flex items-center justify-center";

const ORB_BACKDROP_GLOW_CLASSNAME =
	"size-[calc(1.75rem*1.375-5px)] rounded-full bg-[radial-gradient(circle,rgba(0,0,0,0.55)_0%,rgba(0,0,0,0.38)_32%,rgba(0,0,0,0.16)_55%,transparent_72%)] blur-[calc(1rem*1.75/6)]";

const ORB_LAYER_CLASSNAME = "pointer-events-none absolute inset-0 z-0";

const PLAY_BUTTON_CLASSNAME =
	"relative z-10 size-7 shrink-0 rounded-full bg-background shadow-sm dark:bg-zinc-900";

interface InteractiveAudioTransportOrbProps {
	transportState: InteractiveAudioTransportState;
	isSessionActive: boolean;
	isAtTrackEnd: boolean;
	orbHue: number;
	className?: string;
	playAriaLabel?: string;
	pauseAriaLabel?: string;
	replayAriaLabel?: string;
	onTogglePlayback: () => void;
	onReplay: () => void;
}

export function InteractiveAudioTransportOrb({
	transportState,
	isSessionActive,
	isAtTrackEnd,
	orbHue,
	className,
	playAriaLabel = "Play audio",
	pauseAriaLabel = "Pause audio",
	replayAriaLabel = "Replay audio",
	onTogglePlayback,
	onReplay,
}: InteractiveAudioTransportOrbProps) {
	const showReplay =
		isAtTrackEnd &&
		transportState !== "playing" &&
		transportState !== "loading";

	const icon =
		transportState === "loading" ? (
			<Loader2Icon className="size-3.5 animate-spin" />
		) : transportState === "playing" ? (
			<PauseIcon className="size-3.5" />
		) : showReplay ? (
			<RotateCcwIcon className="size-3.5" />
		) : (
			<PlayIcon className="size-3.5" />
		);

	return (
		<div className={ORB_HOST_CLASSNAME}>
			{isSessionActive ? (
				<>
					<div className={ORB_BACKDROP_CLASSNAME} aria-hidden>
						<div className={ORB_BACKDROP_GLOW_CLASSNAME} />
					</div>
					<div className={ORB_LAYER_CLASSNAME} aria-hidden>
						<Orb
							hue={orbHue}
							hoverIntensity={0.3}
							rotateOnHover
							forceHoverState={transportState === "playing"}
							backgroundColor="transparent"
						/>
					</div>
				</>
			) : null}
			<Button
				type="button"
				size="icon"
				variant="ghost"
				className={cn(PLAY_BUTTON_CLASSNAME, className)}
				aria-label={
					transportState === "playing"
						? pauseAriaLabel
						: showReplay
							? replayAriaLabel
							: playAriaLabel
				}
				disabled={transportState === "loading"}
				onClick={(event) => {
					event.stopPropagation();
					if (showReplay) {
						void onReplay();
					} else {
						void onTogglePlayback();
					}
				}}
			>
				{icon}
			</Button>
		</div>
	);
}
